import {h} from 'preact';
import {Form, Formik} from 'formik';
import invoke from 'lodash.invoke';

import appLogger from '@core/LoggingProvider';
import Button from '@components/common/Button';
import {TextField} from '@components/common/Form/TextField';
import {trimFormData, validateEmail} from '@util/StringUtils';
import Validator from '@util/Validator';
import {LOGIN_URL} from '@util/URLUtils';
import Icon from '@components/common/Icon';
import RegistrationService from '@services/RegistrationService';
import PasswordShowHide from '@components/registration/PasswordShowHide';
import Message from '@components/common/Message';

const formValidator = new Validator( {
    dentistFullName: {
        required: true,
        fullName: true
    },
    dentalLicense: {
        required: true
    },
    emailAddress: {
        required: true,
        email: true
    }
} );

const AccountSetUp =  ( props, context ) =>
    <div>
        <h1 className="font-thin leading-tight text-center text-2xl md:text-4xl mainSubHeading mx-auto">
            Create a New Ultradent Account
        </h1>

        <div className="universalForm-container my-4 mx-auto leading-tight">
            <p className="text-lg font-bold mb-2">Ultradent only sells products to licensed professionals.</p>
            <p className="text-base">All information provided will be verified for accuracy.</p>
            <Formik
                validate={formData => {
                    
                    const {emailAddress, dentistFullName, dentalLicense, password} = trimFormData( context.state );

                    const errors = formValidator.validate( {dentistFullName, dentalLicense, emailAddress} );

                    if ( !password ) {
                        errors.password = 'Password Required.';
                    }
                    else if ( password.length < 8 ) {
                        errors.password = 'Please enter at least 8 characters';
                    }

                    return errors;
                }}
                onSubmit={( values, {setStatus, setSubmitting} ) => {
                    values.loading = true;
                    RegistrationService.checkIfExistingCustomer( context.state.emailAddress,
                        context.state.dentalLicense )
                        .then( resp => {
                            values.loading = false;
                            const {dentalLicenseExists, emailExists, isPending} = resp;

                            const emailAddress = context.state.emailAddress;

                            invoke( context, 'setDentalLicense', dentalLicenseExists );

                            if( isPending ) {
                                invoke( context, 'resetForm' );
                                props.history.push( {pathname: '/accountRequiresVerification', state: {isSubmitted: true, emailAddress}} );
                            }
                            else if ( emailExists ) {
                                setSubmitting( false );
                                values.errors =
                                    'This email is already in use. Please use a different email or sign in below.';
                                setStatus( {
                                    email: 'There was an error with your submission, please correct the errors below.'
                                } );
                            }
                            else if ( !emailExists && dentalLicenseExists ) {
                                invoke( context, 'setStep', 2 );
                                props.history.push( {pathname: '/verifyAccount'} );
                            }
                            else {
                                invoke( context, 'setDecision', true );
                                props.history.push( '/newExistingDecision' );
                            }
                        } )
                        .catch( error => {
                            appLogger.capture( error );
                            setSubmitting( false );
                            values.loading = false;
                            setStatus( {serverError: 'There was an error submitting this request, please try again.'} );
                        } );
                }}
                initialValues={{
                    loading: false,
                    errors: '',
                }}
                render={formik => {
                    const {values, errors, status, handleSubmit, isSubmitting} = formik;
                    return (
                        <Form className="my-4 universalForm">
                            {status &&
                            <Message fail
                                     className="bg-fail flex font-normal items-center -ml-4 -mr-4 md:-ml-8 md:-mr-8 px-4 md:px-8 py-4 text-white w-auto mb-4">
                                <Icon.ui wrapperClassName="mr-2" className="text-white text-xl"
                                         name="report-problem-triangle"/>
                                <span className="message-bar-msg">{status.serverError || status.email}</span>
                            </Message>
                            }
                            <p className="text-xs">* indicates a required field.</p>
                            <label htmlFor="dentistFullName">Dentist First & Last Name*</label>
                            <TextField
                                type="text"
                                name="dentistFullName"
                                value={context.state.dentistFullName}
                                onChange={context.handleChange}
                                inputClassName="w-full"
                                error={errors.dentistFullName}
                                id="dentistFullName"/>
                            <label htmlFor="dentalLicense">Dental License Number*</label>
                            <TextField
                                type="text"
                                name="dentalLicense"
                                value={context.state.dentalLicense}
                                onChange={context.handleChange}
                                inputClassName="w-full"
                                error={errors.dentalLicense}
                                id="dentalLicense"/>
                            <label htmlFor="emailAddress">Email Address*</label>
                            <TextField
                                type="text"
                                name="emailAddress"
                                value={context.state.emailAddress}
                                onChange={context.handleChange}
                                icon={values.errors && 'report-problem-triangle'}
                                iconClassName="text-red text-xl"
                                inputClassName="w-full"
                                error={errors.emailAddress || values.errors}
                                id="emailAddress"/>
                            <label htmlFor="password">Password*</label>
                            <PasswordShowHide
                                {...formik}
                                handleChange={context.handleChange}
                                value={context.state.password}
                            />

                            <div className="flex-row justify-end mt-8 mx-0">
                                <Button primary
                                        loading={values.loading}
                                        disabled={isSubmitting}
                                        className="w-full"
                                        onClick={handleSubmit}
                                        style="flex: initial;">Next</Button>
                            </div>
                        </Form>
                    )
                }}/>

            <h2 className="text-lg text-center mt-16 mb-4">Already have an online account?</h2>
            <div className="flex md:flex-row justify-center items-center">
                <div className="flex-0">
                    <Button as="a" href={LOGIN_URL} tertiary fill>Sign in</Button>
                </div>
                <span className="flex-1 text-center font-bold">OR</span>
                <a href="https://secure.billtrust.com/ultradent/ig/signin" target="_blank"
                   className="flex flex-0 justify-center items-center underline hover:underline md:whitespace-no-wrap">
                    <Icon.ui name="reg-payment" className="text-xl mr-2"/>
                    Make a Payment
                </a>
            </div>
        </div>
    </div>;

export default AccountSetUp;